import { trackError } from '@billy/track-error'
import { useEffect, useState } from 'react'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { Contact } from '@features/contacts/types/contact'

import { usePolling } from '../../../../../hooks/usePolling'

const MAX_CONTACTS_REFETCH_COUNT = 6
const CONTACTS_REFETCH_INTERVAL = 2000

interface UseUpdateContactWithExternalIdProps {
  contactId?: string
  onContactUpdate: (contact: Contact) => void
  enabled: boolean
}

// This hook is intended to be used in scenarios where we need to ensure that a contact has an externalId before proceeding.
// For example, after creating a new contact, we may need to wait until the externalId is populated.
export const useUpdateContactWithExternalId = ({
  contactId,
  onContactUpdate,
  enabled,
}: UseUpdateContactWithExternalIdProps) => {
  const [isResolved, setIsResolved] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setIsResolved(false)
    }
  }, [enabled])

  const { refetch } = useFetchContactById(contactId, {
    enabled: enabled && !!contactId,
    onSuccess: (data) => {
      if (data?.contact?.externalId) {
        onContactUpdate(data.contact)
        setIsResolved(true)
      }
    },
    onError: (error) => {
      trackError(error)
      setIsResolved(true)
    },
  })

  // Poll using refetch from useQuery - this reuses the same query and cache,
  // following the same pattern as useSubjectReconciliationSuggestions.
  usePolling({
    onPoll: refetch,
    interval: CONTACTS_REFETCH_INTERVAL,
    maxAttempts: MAX_CONTACTS_REFETCH_COUNT,
    isEnabled: enabled && !isResolved,
  })

  return { isProcessing: enabled && !isResolved }
}
